import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AdminDashboard from './pages/AdminDashboard';
import AuxiliarDashboard from './pages/AuxiliarDashboard';
import GestorDashboard from './pages/GestorDashboard';
import PublicRegistration from './pages/PublicRegistration';
import PublicPerguntas from './pages/PublicPerguntas';
import PublicVisitas from './pages/PublicVisitas';
import PublicAtualizar from './pages/PublicAtualizar';
import PublicLinkPage from './pages/PublicLinkPage';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import TermosDeUso from './pages/TermosDeUso';
import SobrePage from './pages/SobrePage';
import AgendaPage from './pages/AgendaPage';
import ChangePassword from './pages/ChangePassword';
import BatismoDetailPage from './pages/BatismoDetailPage';
import EbiCoordenadoraDashboard from './pages/EbiCoordenadoraDashboard';
import MovimentosCoordenadorDashboard from './pages/MovimentosCoordenadorDashboard';
import EbiPublicLinkPage from './pages/EbiPublicLinkPage';
import EbiSubmitPage from './pages/EbiSubmitPage';
import EbiTrackPage from './pages/EbiTrackPage';
import EbiCollaboratorSignupPage from './pages/EbiCollaboratorSignupPage';
import EbiStoryDetailPage from './pages/EbiStoryDetailPage';
import EbiStoryPrintPage from './pages/EbiStoryPrintPage';
import MovimentoPortalPage from './pages/MovimentoPortalPage';
import MovimentoFamiliaPublicPage from './pages/MovimentoFamiliaPublicPage';
import MovimentoFamiliaPublicHistoryPage from './pages/MovimentoFamiliaPublicHistoryPage';
import MovimentoMissionarioPublicPage from './pages/MovimentoMissionarioPublicPage';
import MovimentoGrupoPrintPage from './pages/MovimentoGrupoPrintPage';
import MovimentoRelatorioPrintPage from './pages/MovimentoRelatorioPrintPage';
import MovimentoGruposPublicPage from './pages/MovimentoGruposPublicPage';
import MovimentoGrupoViewPublicPage from './pages/MovimentoGrupoViewPublicPage';
import AccessSelector from './pages/AccessSelector';

import { Link2Off } from 'lucide-react';

import { API } from './lib/helpers';
import { getAccessOptions, getRememberedAccess } from './lib/accessOptions';

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Link2Off className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-700 mb-1">Página não encontrada</h2>
        <p className="text-sm text-slate-500">O link que você acessou não existe ou foi desativado.</p>
      </div>
    </div>
  );
}

function AuthRedirect() {
  const [checked, setChecked] = useState(false);
  const [target, setTarget] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.role) {
          setMustChangePassword(!!d.must_change_password);
          const options = getAccessOptions(d);
          const remembered = getRememberedAccess(options);
          setTarget(remembered?.route || (options.length > 1 ? '/acessos' : options[0]?.route));
        }
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, []);
  if (!checked) return null;
  if (mustChangePassword) return <Navigate to="/alterar-senha" replace />;
  if (target) return <Navigate to={target} replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Check URL fragment for session_id synchronously before routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<AuthRedirect />} />
      <Route path="/acessos" element={<AccessSelector />} />
      <Route path="/alterar-senha" element={<ChangePassword />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/auxiliar" element={<AuxiliarDashboard />} />
      <Route path="/gestor" element={<GestorDashboard />} />
      <Route path="/ebi" element={<EbiCoordenadoraDashboard />} />
      <Route path="/movimentos-painel" element={<MovimentosCoordenadorDashboard />} />
      <Route path="/ebi-coordenadora" element={<Navigate to="/ebi" replace />} />
      <Route path="/admin/ebi/historias/:storyId" element={<EbiStoryDetailPage />} />
      <Route path="/ebi/historias/:storyId/imprimir" element={<EbiStoryPrintPage />} />
      <Route path="/ebi/:slug/historias/:storyId/imprimir" element={<EbiStoryPrintPage publicMode />} />
      <Route path="/batismos/:batismoId" element={<BatismoDetailPage />} />
      <Route path="/movimentos/:movementId/grupos/:groupId/imprimir" element={<MovimentoGrupoPrintPage />} />
      <Route path="/movimentos/:movementId/relatorio/imprimir" element={<MovimentoRelatorioPrintPage />} />
      <Route path="/movimentos/relatorio/seguro/:token" element={<MovimentoRelatorioPrintPage secureMode />} />
      <Route path="/movimentos/portal/:token" element={<MovimentoPortalPage />} />
      <Route path="/movimentos/missionarios/publico/:token" element={<MovimentoMissionarioPublicPage />} />
      <Route path="/movimentos/grupos/publico/:token" element={<MovimentoGruposPublicPage />} />
      <Route path="/movimentos/meu-guia/:token" element={<MovimentoGrupoViewPublicPage meuGuiaMode />} />
      <Route path="/movimentos/grupo/publico/:token" element={<MovimentoGrupoViewPublicPage />} />
      <Route path="/movimentos/familias/publico/:token/cadastrados" element={<MovimentoFamiliaPublicHistoryPage />} />
      <Route path="/movimentos/familias/publico/:token" element={<MovimentoFamiliaPublicPage />} />
      <Route path="/ebi/:slug/enviar" element={<EbiSubmitPage />} />
      <Route path="/ebi/:slug/acompanhar" element={<EbiTrackPage />} />
      <Route path="/ebi/:slug/colaboradora" element={<EbiCollaboratorSignupPage />} />
      <Route path="/ebi/:slug" element={<EbiPublicLinkPage />} />
      <Route path="/pessoas" element={<PublicRegistration />} />
      <Route path="/pessoas/:slug" element={<PublicRegistration />} />
      <Route path="/atualizar/:slug" element={<PublicAtualizar />} />
      <Route path="/perguntas" element={<PublicPerguntas />} />
      <Route path="/perguntas/:slug" element={<PublicPerguntas />} />
      <Route path="/visitas/:slug" element={<PublicVisitas />} />
      <Route path="/agenda/:slug" element={<AgendaPage />} />
      <Route path="/l/:slug" element={<PublicLinkPage />} />
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/termos" element={<TermosDeUso />} />
      <Route path="/sobre" element={<SobrePage />} />
      <Route path="/cadastro" element={<NotFoundPage />} />
      <Route path="/cadastro/:slug" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
